import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { 
  Home, Wallet, ArrowUpCircle, ArrowDownCircle, History, Settings, 
  Bell, Copy, RefreshCw, QrCode, Shield, TrendingUp, TrendingDown,
  User, Phone, Mail, Key, ChevronRight, Filter, Calendar, Search,
  Globe, Zap, CreditCard, Gift, Newspaper, BarChart3, ArrowRight
} from "lucide-react";

// Real exchange rates (approximately Feb 2026)
const EXCHANGE_RATES = {
  BTC: 6800000,   // 1 BTC = 6.8M RUB
  ETH: 250000,    // 1 ETH = 250K RUB
  USDT: 76,       // 1 USDT = 76 RUB
  USD: 76         // 1 USD = 76 RUB
};

const CryptoIcon = ({ symbol, size = "w-8 h-8" }) => {
  const icons = {
    BTC: { bg: 'linear-gradient(135deg, #F7931A 0%, #FF9500 100%)', text: '₿' },
    ETH: { bg: 'linear-gradient(135deg, #627EEA 0%, #8B9FE8 100%)', text: 'Ξ' },
    USDT: { bg: 'linear-gradient(135deg, #26A17B 0%, #3CC68A 100%)', text: '$' },
    RUB: { bg: 'linear-gradient(135deg, #4A5568 0%, #718096 100%)', text: '₽' },
  };
  const icon = icons[symbol] || { bg: '#666', text: '?' };
  return (
    <div className={`${size} rounded-full flex items-center justify-center text-white font-bold text-sm shadow-lg`} style={{ background: icon.bg }}>
      {icon.text}
    </div>
  );
};

// Format number with spaces
const formatNumber = (num, decimals = 2) => {
  return parseFloat(num).toLocaleString('ru-RU', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
};

// Convert crypto to RUB using real rates
const toRub = (amount, currency) => {
  const rate = EXCHANGE_RATES[currency] || 1;
  return parseFloat(amount) * rate;
};

export default function CoinPayCombinedPreview() {
  const [searchParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [browserTheme, setBrowserTheme] = useState('light');
  const [showBrowser, setShowBrowser] = useState(true);
  const [currentPage, setCurrentPage] = useState('main');
  const [historyFilter, setHistoryFilter] = useState('all'); // all, incoming, outgoing
  const [dateFrom, setDateFrom] = useState(null); // start date filter - will be set from transactions
  const [dateTo, setDateTo] = useState(null); // end date filter - will be set from transactions

  // Parse date from DD.MM.YYYY to Date object
  const parseDate = (dateStr) => {
    if (!dateStr) return new Date();
    const [day, month, year] = dateStr.split('.');
    return new Date(year, month - 1, day);
  };

  // Format date for input (YYYY-MM-DD)
  const formatDateForInput = (date) => {
    const d = new Date(date);
    return d.toISOString().split('T')[0];
  };

  useEffect(() => {
    const dataParam = searchParams.get('data');
    const themeParam = searchParams.get('theme');
    const browserParam = searchParams.get('browser');
    if (themeParam) setBrowserTheme(themeParam);
    if (browserParam !== null) setShowBrowser(browserParam !== 'false');
    if (dataParam) {
      try {
        const parsedData = JSON.parse(decodeURIComponent(dataParam));
        setData(parsedData);
        if (parsedData.showBrowser !== undefined) setShowBrowser(parsedData.showBrowser);
        // Set date filters from passed data or calculate from transactions
        if (parsedData.dateFrom && parsedData.dateTo) {
          setDateFrom(parsedData.dateFrom);
          setDateTo(parsedData.dateTo);
        } else if (parsedData.transactions && parsedData.transactions.length > 0) {
          // Calculate from transactions - keep DD.MM.YYYY format
          const validDates = parsedData.transactions
            .map(tx => {
              if (!tx.date) return null;
              const parts = tx.date.split('.');
              if (parts.length !== 3) return null;
              const [day, month, year] = parts;
              return { 
                original: tx.date, 
                sortValue: parseInt(year) * 10000 + parseInt(month) * 100 + parseInt(day) 
              };
            })
            .filter(d => d !== null);
          
          if (validDates.length > 0) {
            validDates.sort((a, b) => a.sortValue - b.sortValue);
            setDateFrom(validDates[0].original);
            setDateTo(validDates[validDates.length - 1].original);
          }
        }
      } catch (e) {
        console.error("Error parsing data:", e);
      }
    }
  }, [searchParams]);

  useEffect(() => {
    const titles = { main: 'Кошелёк', history: 'История', settings: 'Настройки' };
    document.title = `${titles[currentPage]} | CoinPay`;
    let favicon = document.querySelector("link[rel='icon']");
    if (!favicon) {
      favicon = document.createElement('link');
      favicon.rel = 'icon';
      document.head.appendChild(favicon);
    }
    favicon.href = '/coinpay-favicon.svg';
  }, [currentPage]);

  if (!data) {
    return <div className="min-h-screen bg-gray-100 flex items-center justify-center">Загрузка...</div>;
  }

  const isDark = browserTheme === 'dark';
  const theme = {
    bg: isDark ? '#202124' : '#dee1e6',
    tabBarBg: isDark ? '#202124' : '#dee1e6',
    activeTabBg: isDark ? '#292a2d' : '#ffffff',
    inactiveTabBg: isDark ? '#35363a' : '#c9cdd4',
    addressBarBg: isDark ? '#292a2d' : '#ffffff',
    textPrimary: isDark ? '#e8eaed' : '#202124',
    textSecondary: isDark ? '#9aa0a6' : '#5f6368',
    border: isDark ? '#3c4043' : '#dadce0',
  };

  const accountName = `${data.firstName || ''} ${data.lastName || ''}`.trim() || 'Пользователь';
  const userId = data.visibleId || '000000';

  // Calculate real balances using exchange rates
  const cryptoBalances = [
    { 
      symbol: 'BTC', 
      name: 'Bitcoin', 
      balance: data.btcBalance || '0.000000',
      change: '+2.4%',
      changePositive: true
    },
    { 
      symbol: 'ETH', 
      name: 'Ethereum', 
      balance: data.ethBalance || '0.0000',
      change: '-1.2%',
      changePositive: false
    },
    { 
      symbol: 'USDT', 
      name: 'Tether', 
      balance: data.usdtBalance || '0.00',
      change: '+0.1%',
      changePositive: true
    },
    { 
      symbol: 'RUB', 
      name: 'Рубли', 
      balance: data.balance || '0',
      change: '0%',
      changePositive: true
    },
  ];

  // Calculate total in RUB
  const totalRub = cryptoBalances.reduce((sum, c) => {
    if (c.symbol === 'RUB') return sum + parseFloat(c.balance || 0);
    return sum + toRub(c.balance, c.symbol);
  }, 0);

  const menuItems = [
    { id: 'main', icon: Home, label: 'Главная' },
    { id: 'wallet', icon: Wallet, label: 'Кошелёк' },
    { id: 'exchange', icon: RefreshCw, label: 'Обмен' },
    { id: 'history', icon: History, label: 'История' },
    { id: 'cards', icon: CreditCard, label: 'Карты' },
    { id: 'settings', icon: Settings, label: 'Настройки' },
  ];

  const news = [
    { title: 'Bitcoin достиг нового максимума', time: '2 часа назад', positive: true },
    { title: 'Ethereum обновил сеть до версии 3.0', time: '5 часов назад', positive: true },
    { title: 'ЦБ РФ рассматривает регулирование крипто', time: 'вчера', positive: false },
  ];

  const BrowserFrame = () => (
    <div style={{ backgroundColor: theme.tabBarBg }} className="flex-shrink-0">
      <div className="flex items-center h-[38px]" style={{ backgroundColor: theme.tabBarBg }}>
        <div className="flex items-end flex-1 pl-2">
          <div className="flex items-center h-[34px] px-3 rounded-t-lg min-w-[140px] max-w-[200px] mr-[1px] cursor-pointer" style={{ backgroundColor: theme.inactiveTabBg }}>
            <img src="/ozon-favicon.ico" alt="" className="w-4 h-4 mr-2 flex-shrink-0" />
            <span className="text-[13px] truncate flex-1" style={{ color: theme.textSecondary }}>Ozon Карта</span>
          </div>
          <div className="flex items-center h-[34px] px-3 rounded-t-lg min-w-[180px] max-w-[240px]" style={{ backgroundColor: theme.activeTabBg }}>
            <img src="/coinpay-favicon.svg" alt="" className="w-4 h-4 mr-2 flex-shrink-0" />
            <span className="text-[13px] truncate flex-1" style={{ color: theme.textPrimary }}>CoinPay</span>
            <span className="ml-2 text-[13px]" style={{ color: theme.textSecondary }}>×</span>
          </div>
        </div>
      </div>
      <div className="flex items-center h-[40px] px-4 gap-4" style={{ backgroundColor: theme.addressBarBg }}>
        <div className="flex gap-2">
          {['←', '→', '↻'].map((arrow, i) => (
            <button key={i} className="w-8 h-8 rounded-full flex items-center justify-center" style={{ color: theme.textSecondary }}>
              {arrow}
            </button>
          ))}
        </div>
        <div className="flex-1 h-[32px] rounded-full px-4 flex items-center" style={{ backgroundColor: isDark ? '#202124' : '#f1f3f4' }}>
          <span className="text-[14px]" style={{ color: theme.textSecondary }}>🔒 coinpay.io</span>
        </div>
      </div>
    </div>
  );

  const MainPage = () => {
    // Get transactions from OZON profile (passed via URL data)
    const transactions = data.transactions || [];
    
    const totalIncoming = transactions
      .filter(tx => tx.type === 'deposit')
      .reduce((sum, tx) => sum + parseFloat(tx.amount || 0), 0);
    
    const totalOutgoing = transactions
      .filter(tx => tx.type === 'withdrawal')
      .reduce((sum, tx) => sum + parseFloat(tx.amount || 0), 0);

    return (
    <div className="p-6 space-y-6">
      {/* Balance Card */}
      <div className="bg-gradient-to-br from-emerald-500 via-green-500 to-teal-600 rounded-2xl p-6 text-white shadow-xl">
        <div className="flex justify-between items-start mb-4">
          <div>
            <div className="text-sm opacity-80 mb-1">Общий баланс</div>
            <div className="text-4xl font-bold">{formatNumber(totalRub, 0)} ₽</div>
            <div className="text-sm opacity-80 mt-2">≈ ${formatNumber(totalRub / EXCHANGE_RATES.USD, 2)} USD</div>
          </div>
          <div className="flex items-center gap-1 bg-white/20 rounded-full px-3 py-1">
            <TrendingUp className="w-4 h-4" />
            <span className="text-sm font-medium">+3.2%</span>
          </div>
        </div>
        
        {/* Incoming/Outgoing Stats */}
        <div className="grid grid-cols-2 gap-3 mt-4 mb-4">
          <div className="bg-white/15 rounded-xl p-3">
            <div className="flex items-center gap-2 mb-1">
              <ArrowDownCircle className="w-4 h-4" />
              <span className="text-xs opacity-80">Входящие</span>
            </div>
            <div className="text-lg font-bold">+{formatNumber(totalIncoming, 0)} ₽</div>
          </div>
          <div className="bg-white/15 rounded-xl p-3">
            <div className="flex items-center gap-2 mb-1">
              <ArrowUpCircle className="w-4 h-4" />
              <span className="text-xs opacity-80">Вывод</span>
            </div>
            <div className="text-lg font-bold">-{formatNumber(totalOutgoing, 0)} ₽</div>
          </div>
        </div>
        
        <div className="grid grid-cols-4 gap-2">
          {[
            { icon: ArrowUpCircle, label: 'Пополнить', color: 'bg-white/20' },
            { icon: ArrowDownCircle, label: 'Вывести', color: 'bg-white/20' },
            { icon: RefreshCw, label: 'Обмен', color: 'bg-white/20' },
            { icon: QrCode, label: 'QR-код', color: 'bg-white/20' },
          ].map((btn, i) => (
            <button key={i} className={`${btn.color} rounded-xl p-3 flex flex-col items-center gap-1 hover:bg-white/30 transition-colors`}>
              <btn.icon className="w-5 h-5" />
              <span className="text-xs">{btn.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-white rounded-xl p-4 shadow-sm">
          <div className="flex items-center gap-2 mb-2">
            <BarChart3 className="w-4 h-4 text-blue-500" />
            <span className="text-xs text-gray-500">За месяц</span>
          </div>
          <div className="text-lg font-bold text-gray-800">+{formatNumber(totalRub * 0.12, 0)} ₽</div>
          <div className="text-xs text-green-500">+12% прибыль</div>
        </div>
        <div className="bg-white rounded-xl p-4 shadow-sm">
          <div className="flex items-center gap-2 mb-2">
            <Zap className="w-4 h-4 text-yellow-500" />
            <span className="text-xs text-gray-500">Транзакций</span>
          </div>
          <div className="text-lg font-bold text-gray-800">{(data.transactions || []).length || 12}</div>
          <div className="text-xs text-gray-500">за 30 дней</div>
        </div>
        <div className="bg-white rounded-xl p-4 shadow-sm">
          <div className="flex items-center gap-2 mb-2">
            <Gift className="w-4 h-4 text-purple-500" />
            <span className="text-xs text-gray-500">Кэшбэк</span>
          </div>
          <div className="text-lg font-bold text-gray-800">{formatNumber(totalRub * 0.01, 0)} ₽</div>
          <div className="text-xs text-purple-500">накоплено</div>
        </div>
      </div>

      {/* Assets */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
          <span className="font-semibold text-gray-800">Мои активы</span>
          <button className="text-green-500 text-sm font-medium flex items-center gap-1">
            Все <ArrowRight className="w-4 h-4" />
          </button>
        </div>
        {cryptoBalances.map((crypto, i) => (
          <div key={i} className="px-4 py-4 flex items-center justify-between border-b border-gray-50 last:border-0 hover:bg-gray-50 transition-colors">
            <div className="flex items-center gap-3">
              <CryptoIcon symbol={crypto.symbol} />
              <div>
                <div className="font-medium text-gray-800">{crypto.name}</div>
                <div className="text-sm text-gray-500">{crypto.symbol}</div>
              </div>
            </div>
            <div className="text-right">
              <div className="font-semibold text-gray-800">{crypto.balance} {crypto.symbol}</div>
              <div className="text-sm text-gray-500">
                {formatNumber(crypto.symbol === 'RUB' ? crypto.balance : toRub(crypto.balance, crypto.symbol), 0)} ₽
              </div>
            </div>
            <div className={`text-sm font-medium ${crypto.changePositive ? 'text-green-500' : 'text-red-500'}`}>
              {crypto.change}
            </div>
          </div>
        ))}
      </div>

      {/* Market News */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-100 flex items-center gap-2">
          <Newspaper className="w-5 h-5 text-blue-500" />
          <span className="font-semibold text-gray-800">Новости рынка</span>
        </div>
        {news.map((item, i) => (
          <div key={i} className="px-4 py-3 flex items-center justify-between border-b border-gray-50 last:border-0 hover:bg-gray-50 transition-colors cursor-pointer">
            <div className="flex items-center gap-3">
              <div className={`w-2 h-2 rounded-full ${item.positive ? 'bg-green-500' : 'bg-red-500'}`} />
              <span className="text-gray-800">{item.title}</span>
            </div>
            <span className="text-xs text-gray-400">{item.time}</span>
          </div>
        ))}
      </div>

      {/* Exchange Rates */}
      <div className="bg-gradient-to-r from-gray-800 to-gray-900 rounded-xl p-4 text-white">
        <div className="flex items-center justify-between mb-3">
          <span className="font-medium">Курсы валют</span>
          <span className="text-xs text-gray-400">обновлено только что</span>
        </div>
        <div className="grid grid-cols-3 gap-4">
          <div>
            <div className="text-xs text-gray-400">BTC/RUB</div>
            <div className="font-bold">{formatNumber(EXCHANGE_RATES.BTC, 0)}</div>
          </div>
          <div>
            <div className="text-xs text-gray-400">ETH/RUB</div>
            <div className="font-bold">{formatNumber(EXCHANGE_RATES.ETH, 0)}</div>
          </div>
          <div>
            <div className="text-xs text-gray-400">USDT/RUB</div>
            <div className="font-bold">{formatNumber(EXCHANGE_RATES.USDT, 2)}</div>
          </div>
        </div>
      </div>
    </div>
  );
  };

  const HistoryPage = () => {
    // Get transactions from OZON profile (already in RUB)
    const transactions = data.transactions || [];
    
    if (transactions.length === 0) {
      return (
        <div className="p-6">
          <h2 className="text-xl font-bold text-gray-800 mb-4">История операций</h2>
          <div className="bg-white rounded-xl p-8 text-center text-gray-500">
            Нет операций в профиле OZON
          </div>
        </div>
      );
    }
    
    // Convert amounts to numbers and sort by date (newest first)
    const transactionsWithAmount = transactions.map(tx => ({
      ...tx,
      amountNum: parseFloat(tx.amount || 0),
      dateObj: parseDate(tx.date)
    })).sort((a, b) => b.dateObj - a.dateObj);
    
    // Get min and max dates from transactions
    const dates = transactionsWithAmount.map(tx => tx.dateObj);
    const minDate = new Date(Math.min(...dates));
    const maxDate = new Date(Math.max(...dates));
    
    // Use globally set dates (they are initialized from transactions on load)
    const effectiveDateFrom = dateFrom || formatDateForInput(minDate);
    const effectiveDateTo = dateTo || formatDateForInput(maxDate);
    
    // Calculate totals in RUB
    const totalIncoming = transactionsWithAmount
      .filter(tx => tx.type === 'deposit')
      .reduce((sum, tx) => sum + tx.amountNum, 0);
    
    const totalOutgoing = transactionsWithAmount
      .filter(tx => tx.type === 'withdrawal')
      .reduce((sum, tx) => sum + tx.amountNum, 0);

    // Apply date range filter FIRST (for statistics)
    const fromDate = effectiveDateFrom ? new Date(effectiveDateFrom) : minDate;
    const toDate = effectiveDateTo ? new Date(effectiveDateTo) : maxDate;
    
    const dateFiltered = transactionsWithAmount.filter(tx => {
      return tx.dateObj >= fromDate && tx.dateObj <= toDate;
    });
    
    // Calculate totals for the entire date period (NOT affected by type filter)
    const periodIncoming = dateFiltered
      .filter(tx => tx.type === 'deposit')
      .reduce((sum, tx) => sum + tx.amountNum, 0);
    
    const periodOutgoing = dateFiltered
      .filter(tx => tx.type === 'withdrawal')
      .reduce((sum, tx) => sum + tx.amountNum, 0);

    // Apply type filter for display list only
    let filtered = dateFiltered;
    if (historyFilter === 'incoming') {
      filtered = filtered.filter(tx => tx.type === 'deposit');
    } else if (historyFilter === 'outgoing') {
      filtered = filtered.filter(tx => tx.type === 'withdrawal');
    }

    return (
      <div className="p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-bold text-gray-800">История операций</h2>
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-500">{filtered.length} операций</span>
          </div>
        </div>
        
        {/* Balance Stats - Always shows totals for entire date period */}
        <div className="grid grid-cols-3 gap-4">
          <div className="bg-white rounded-xl p-4 shadow-sm">
            <div className="flex items-center gap-2 mb-2">
              <ArrowDownCircle className="w-4 h-4 text-green-500" />
              <span className="text-xs text-gray-500">Входящие</span>
            </div>
            <div className="text-lg font-bold text-green-600">+{formatNumber(periodIncoming, 0)} ₽</div>
          </div>
          <div className="bg-white rounded-xl p-4 shadow-sm">
            <div className="flex items-center gap-2 mb-2">
              <ArrowUpCircle className="w-4 h-4 text-red-500" />
              <span className="text-xs text-gray-500">Вывод</span>
            </div>
            <div className="text-lg font-bold text-red-600">-{formatNumber(periodOutgoing, 0)} ₽</div>
          </div>
          <div className="bg-white rounded-xl p-4 shadow-sm">
            <div className="flex items-center gap-2 mb-2">
              <BarChart3 className="w-4 h-4 text-blue-500" />
              <span className="text-xs text-gray-500">Баланс</span>
            </div>
            <div className={`text-lg font-bold ${periodIncoming - periodOutgoing >= 0 ? 'text-green-600' : 'text-red-600'}`}>
              {periodIncoming - periodOutgoing >= 0 ? '+' : ''}{formatNumber(periodIncoming - periodOutgoing, 0)} ₽
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl p-4 shadow-sm space-y-3">
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-gray-400" />
            <span className="text-sm font-medium text-gray-700">Фильтры</span>
          </div>
          
          <div className="flex flex-wrap gap-3 items-center">
            {/* Type filter */}
            <div className="flex bg-gray-100 rounded-lg p-1">
              {[
                { id: 'all', label: 'Все' },
                { id: 'incoming', label: 'Исходящие' },
                { id: 'outgoing', label: 'Входящие' },
              ].map(f => (
                <button
                  key={f.id}
                  onClick={() => setHistoryFilter(f.id)}
                  className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                    historyFilter === f.id
                      ? 'bg-white text-green-600 shadow-sm'
                      : 'text-gray-600 hover:text-gray-800'
                  }`}
                >
                  {f.label}
                </button>
              ))}
            </div>
            
            {/* Date Range Picker */}
            <div className="flex items-center gap-2 px-4 py-2 bg-white rounded-lg border border-gray-200">
              <Calendar className="w-4 h-4 text-gray-400" />
              <span className="text-sm text-gray-500">с</span>
              <span className="text-sm text-gray-700 font-medium">
                {effectiveDateFrom || '—'}
              </span>
              <span className="text-sm text-gray-500">по</span>
              <span className="text-sm text-gray-700 font-medium">
                {effectiveDateTo || '—'}
              </span>
            </div>
          </div>
        </div>

        {/* Transactions List */}
        <div className="bg-white rounded-xl shadow-sm divide-y divide-gray-100">
          {filtered.length === 0 ? (
            <div className="p-8 text-center text-gray-500">Нет операций по выбранным фильтрам</div>
          ) : (
            filtered.map((tx, i) => (
              <div key={i} className="p-4 flex items-center justify-between hover:bg-gray-50 transition-colors">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                    tx.type === 'deposit' ? 'bg-green-100' : 'bg-red-100'
                  }`}>
                    {tx.type === 'deposit' 
                      ? <ArrowDownCircle className="w-5 h-5 text-green-600" /> 
                      : <ArrowUpCircle className="w-5 h-5 text-red-600" />
                    }
                  </div>
                  <div>
                    <div className="font-medium text-gray-800">
                      {tx.method || (tx.type === 'deposit' ? 'Пополнение' : 'Вывод')}
                    </div>
                    <div className="text-sm text-gray-500">{tx.date} в {tx.time}</div>
                  </div>
                </div>
                <div className="text-right">
                  <div className={`font-semibold text-lg ${tx.type === 'deposit' ? 'text-green-600' : 'text-red-600'}`}>
                    {tx.type === 'deposit' ? '+' : '-'}{formatNumber(tx.amountNum, 0)} ₽
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    );
  };

  const SettingsPage = () => (
    <div className="p-6 space-y-4">
      <h2 className="text-xl font-bold text-gray-800">Настройки профиля</h2>
      
      {/* Profile Card */}
      <div className="bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl p-4 text-white">
        <div className="flex items-center gap-4">
          <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center text-2xl font-bold">
            {accountName.charAt(0)}
          </div>
          <div>
            <div className="font-bold text-lg">{accountName}</div>
            <div className="text-sm opacity-80">ID: {userId}</div>
            <div className="flex items-center gap-1 mt-1">
              <Shield className="w-4 h-4" />
              <span className="text-sm">Верифицирован</span>
            </div>
          </div>
        </div>
      </div>

      {/* Settings List */}
      <div className="bg-white rounded-xl shadow-sm divide-y divide-gray-100">
        {[
          { icon: User, label: 'Имя', value: accountName },
          { icon: Mail, label: 'Email', value: data.email },
          { icon: Phone, label: 'Телефон', value: data.phone },
          { icon: Key, label: 'ID пользователя', value: userId },
          { icon: Globe, label: 'Язык', value: 'Русский' },
          { icon: Bell, label: 'Уведомления', value: 'Включены' },
        ].map((item, i) => (
          <div key={i} className="p-4 flex items-center justify-between hover:bg-gray-50 transition-colors cursor-pointer">
            <div className="flex items-center gap-3">
              <item.icon className="w-5 h-5 text-gray-400" />
              <span className="text-gray-600">{item.label}</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-gray-800">{item.value}</span>
              <ChevronRight className="w-4 h-4 text-gray-400" />
            </div>
          </div>
        ))}
      </div>

      {/* Verification Status */}
      <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl shadow-sm p-4 border border-green-200">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center">
            <Shield className="w-6 h-6 text-white" />
          </div>
          <div className="flex-1">
            <div className="font-semibold text-green-800">Аккаунт верифицирован</div>
            <div className="text-sm text-green-600">Полный доступ ко всем функциям</div>
          </div>
          <div className="bg-green-500 text-white text-xs font-bold px-3 py-1 rounded-full">
            ✓ Подтверждён
          </div>
        </div>
      </div>

      {/* Security */}
      <div className="bg-white rounded-xl shadow-sm p-4">
        <div className="flex items-center gap-3 mb-3">
          <Shield className="w-5 h-5 text-green-500" />
          <span className="font-medium text-gray-800">Безопасность</span>
        </div>
        <div className="space-y-2">
          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-600">Двухфакторная аутентификация</span>
            <span className="text-green-500 font-medium">Включена</span>
          </div>
          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-600">Последний вход</span>
            <span className="text-gray-500">сегодня, {new Date().toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })}</span>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen template-preview-scale" style={{ backgroundColor: theme.bg }}>
      {showBrowser && <BrowserFrame />}
      <div className="flex" style={{ minHeight: showBrowser ? 'calc(100vh - 78px)' : '100vh' }}>
        {/* Sidebar */}
        <div className="w-64 bg-white border-r border-gray-200 flex flex-col">
          <div className="p-4 border-b border-gray-100">
            <div className="flex items-center">
              <img src="/coinpay-logo.svg" alt="CoinPay" className="h-9 w-auto" style={{ maxWidth: '180px' }} />
            </div>
          </div>
          
          <div className="flex-1 py-2">
            {menuItems.map((item) => (
              <button
                key={item.id}
                onClick={() => setCurrentPage(item.id)}
                className={`w-full px-4 py-3 flex items-center gap-3 transition-colors ${
                  currentPage === item.id
                    ? 'bg-green-50 text-green-600 border-r-2 border-green-500'
                    : 'text-gray-600 hover:bg-gray-50'
                }`}
              >
                <item.icon className="w-5 h-5" />
                <span>{item.label}</span>
              </button>
            ))}
          </div>
          
          {/* User Info */}
          <div className="p-4 border-t border-gray-100">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                <span className="text-green-600 font-bold">{accountName.charAt(0)}</span>
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-medium text-gray-800 truncate">{accountName}</div>
                <div className="text-xs text-gray-500">ID: {userId}</div>
              </div>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 bg-gray-50 overflow-auto">
          {(currentPage === 'main' || currentPage === 'wallet' || currentPage === 'exchange' || currentPage === 'cards') && <MainPage />}
          {currentPage === 'history' && <HistoryPage />}
          {currentPage === 'settings' && <SettingsPage />}
        </div>
      </div>
    </div>
  );
}
